import { useState } from 'react'
import { useTransitionNavigate } from '../../lib/viewTransition'
import { fbt } from 'fbtee'
import { Plus } from 'lucide-react'
import { ConfirmDialog } from '../../components/ConfirmDialog'
import { GroupUpsellCard } from '../../components/GroupUpsellCard'
import { LinkGroupToEventDialog } from '../../components/LinkGroupToEventDialog'
import { Section } from '../../components/Section'
import { Button } from '../../components/ui/button'
import type { Group, UserProfile } from '../../types'
import { LinkGroupPromptCard } from './LinkGroupPromptCard'
import { LinkedGroupRow } from './LinkedGroupRow'

export function GroupsSection({
  eventId,
  isFestival,
  groups,
  linkedGroups,
  goingUserIds,
  interestedUserIds,
  profiles,
  isGuest,
  onUnlink,
  canUnlink,
  onCreateGroup,
}: {
  eventId: string
  isFestival: boolean
  groups: Group[]
  linkedGroups: Group[]
  goingUserIds: ReadonlySet<string>
  interestedUserIds: ReadonlySet<string>
  profiles: Record<string, UserProfile>
  isGuest: boolean
  onUnlink: (groupId: string) => void
  /** Admins, or the member who made the link — others only open the group. */
  canUnlink: (groupId: string) => boolean
  onCreateGroup: () => void
}) {
  const navigate = useTransitionNavigate()
  const [pickerOpen, setPickerOpen] = useState(false)
  const [unlinking, setUnlinking] = useState<Group | null>(null)

  const rows = linkedGroups
    .map(group => ({
      group,
      goingIds: new Set(group.memberIds.filter(uid => goingUserIds.has(uid))),
      interestedCount: group.memberIds.filter(uid => interestedUserIds.has(uid)).length,
    }))
    .sort((a, b) =>
      b.goingIds.size - a.goingIds.size ||
      b.interestedCount - a.interestedCount ||
      a.group.name.localeCompare(b.group.name),
    )

  // The picker offers create itself when there's nothing to pick, so only
  // guests skip it (for the sign-up prompt). Deciding on groups.length sent a
  // tap made while the groups were still loading straight to create.
  const addGroup = () => (isGuest ? onCreateGroup() : setPickerOpen(true))

  return (
    <Section
      title={fbt('Groups', 'Section heading for groups in the social tab of the event page')}
      action={
        <Button
          variant="outline"
          size="icon"
          className="size-7"
          onClick={addGroup}
          aria-label={fbt('Add a group to this event', 'Accessible label for the + button on the event page Groups section')}
        >
          <Plus className="size-3.5" />
        </Button>
      }
    >
      {rows.length > 0 ? (
        <div>
          {rows.map(({ group, goingIds, interestedCount }) => (
            <LinkedGroupRow
              key={group.id}
              group={group}
              goingIds={goingIds}
              interestedCount={interestedCount}
              profiles={profiles}
              onOpen={() => navigate(`/groups/${group.id}`, { state: { entryPoint: 'event' } })}
              onUnlink={isGuest || !canUnlink(group.id) ? undefined : () => setUnlinking(group)}
            />
          ))}
        </div>
      ) : groups.length > 0 ? (
        <LinkGroupPromptCard groups={groups} isFestival={isFestival} onPick={() => setPickerOpen(true)} />
      ) : (
        <GroupUpsellCard onClick={onCreateGroup} />
      )}

      {pickerOpen && (
        <LinkGroupToEventDialog eventId={eventId} entryPoint="event_page" onClose={() => setPickerOpen(false)} />
      )}

      <ConfirmDialog
        open={!!unlinking}
        onOpenChange={open => !open && setUnlinking(null)}
        title={fbt(
          'Unlink ' + fbt.param('group', unlinking?.name ?? '') + '?',
          'Title of the dialog confirming a group is unlinked from the event',
        )}
        description={fbt(
          "The event leaves the group's plans for everyone in it, and any totem spots placed here are cleared. " +
            "Everyone's own going and interested marks stay.",
          'Description of the dialog confirming a group is unlinked from the event',
        )}
        confirmLabel={fbt('Unlink', 'Confirm button that unlinks a group from the event')}
        onConfirm={() => {
          if (unlinking) onUnlink(unlinking.id)
          setUnlinking(null)
        }}
      />
    </Section>
  )
}
